// auth namespace. Backs window.backlit.auth.me() and .users().
//
// me() never throws — an anonymous or signed-out visitor resolves to null
// (a Public glow's allowlisted admin who signed in resolves their user like
// any other glow). The server's /_backlit/auth/me endpoint encodes the same
// contract (200 with the 5-field user object, or a bare JSON null).
//
// users() is the deliberate counterpoint: it THROWS BacklitError on
// failure. It is a privileged, admin-only operator read, so a caller must
// be able to tell "forbidden" / "unauthenticated" apart from an empty
// roster — swallowing to [] would erase that distinction.

import { getBackend, type BacklitUser, type ListOptions, type ListPage } from "./backend.js";
import { isBacklitHost } from "./host.js";

// sanitizeReturnPath reduces a caller-supplied return target to a SAME-ORIGIN
// path reference before it rides in a top-level navigation — defense against
// open-redirect / `javascript:` injection (the server re-validates too). A
// missing/invalid value collapses to "/". Mirrors glowrequest.SafeReturnPath on
// the server (CLAUDE.md §6).
function sanitizeReturnPath(raw: string | undefined): string {
  if (!raw) return "/";
  if (raw[0] !== "/") return "/"; // must be an absolute path reference
  if (raw.startsWith("//") || raw.startsWith("/\\")) return "/"; // protocol-relative / backslash trick
  if (raw.includes("\\") || /[\r\n]/.test(raw)) return "/"; // header/redirect injection
  return raw;
}

// currentPath is the default return target for login()/logout(): the page the
// FAB button was clicked on. Falls back to "/" off-browser.
function currentPath(): string {
  const loc = (globalThis as { location?: Location }).location;
  if (!loc) return "/";
  return (loc.pathname || "/") + (loc.search || "");
}

// navigateTo performs the top-level navigation. SameSite=Strict session cookies
// require a real top-level navigation (not fetch), so login/logout assign
// window.location rather than XHR. No-op when there is no location to assign.
function navigateTo(url: string): void {
  const g = globalThis as { location?: { href: string } };
  if (g.location) g.location.href = url;
}

// beforeLogout is the one pre-navigation hook logout() runs (backlit.offline
// registers its logout sequence: sync, quarantine, delete the on-device copy).
// logout() navigates exactly once: when the hook settles (resolve or reject),
// or after capMs — whichever comes first — so a stuck hook can never trap the
// visitor on the page. logoutRun is the sequence in progress: a second
// logout() meanwhile joins it (one hook run, one navigation).
let beforeLogout: { fn: () => Promise<void>; capMs: number } | null = null;
let logoutRun: Promise<void> | null = null;

// setBeforeLogout installs (or, with null, removes) the logout hook. Internal.
export function setBeforeLogout(fn: (() => Promise<void>) | null, capMs = 1000): void {
  beforeLogout = fn ? { fn, capMs } : null;
}

// _logoutSettledForTest resolves once the logout in progress has navigated.
export const _logoutSettledForTest = (): Promise<void> => logoutRun ?? Promise.resolve();

export function _resetLogoutForTest(): void {
  logoutRun = null;
}

export interface AuthNamespace {
  // me() reports the signed-in user's identity — { uid, name, email, mobile,
  // permission } — or null for an anonymous / signed-out visitor and on
  // network failures. Never throws. Absent uid/name/mobile render as "" and an
  // absent/unrecognized permission as "" (BacklitUser is all-string). The
  // "viewer" permission's data.put / data.delete are rejected server-side with
  // BacklitError(code:"forbidden"); apps can branch on me() (e.g. hide a Save
  // button) rather than letting the write fail.
  //
  // D4: with backlit.offline enabled, a network failure resolves the cached
  // identity instead of null — the last user this device saw, served only
  // while that identity's session proof is under 7 days old (the offline
  // backend's authMe; online behaviour is unchanged).
  me(): Promise<BacklitUser | null>;

  /**
   * Return one cursor-paginated page of the glow's user roster as a
   * {@link ListPage} of {@link BacklitUser} — `{ items, next }`, the same
   * shape the `data` / `userdata` / `records` / `capture` `list()` surfaces
   * resolve to. Pass `opts` ({@link ListOptions}: `{ cursor?, limit? }`) to
   * page; `cursor` is a prior page's `next` (omit for the first page) and
   * `limit` is the requested page size (clamped server-side). Paginate by
   * re-calling with the previous page's `next` until it is `""`.
   * **Admin only**, and **read-only** (roster edits go through the console).
   * Unlike {@link AuthNamespace.me}, this THROWS `BacklitError` on failure: a
   * non-admin (or signed-out / Public) caller gets `forbidden` /
   * `unauthenticated`, a bad cursor gets `invalid_cursor`, an off-host load
   * gets `unsupported_host`, and a transport failure gets `network`. The throw
   * is intentional — an admin must distinguish a denial from an empty roster.
   * A malformed/missing roster body tolerates to `{ items: [], next: "" }`
   * (it does not throw).
   */
  users(opts?: ListOptions): Promise<ListPage<BacklitUser>>;

  /**
   * Send the visitor to sign in. This is a top-level navigation (the page
   * unloads), so it returns `void`, not a Promise. `returnPath` is where the
   * visitor lands back after signin — an absolute same-origin path (default:
   * the current page); anything else is sanitised to `/`. Off a deployed glow
   * host it is a silent no-op (there is nowhere to navigate). Useful for a
   * "Sign in" button on an Accounts glow.
   */
  login(returnPath?: string): void;

  /**
   * Sign the visitor out and return them to `returnPath` (an absolute
   * same-origin path; default the current page; otherwise sanitised to `/`).
   * Top-level navigation, returns `void`. Off a deployed glow host it is a
   * silent no-op. With backlit.offline enabled, the navigation may wait up to
   * ~3 s: queued writes get a brief chance to send, any still unsent are kept
   * for that same account for 30 days, and the offline copy is deleted
   * (bounded — the navigation never waits past a 3.5 s cap). A second call
   * meanwhile joins the first.
   */
  logout(returnPath?: string): void;
}

export function createAuth(): AuthNamespace {
  return {
    async me(): Promise<BacklitUser | null> {
      try {
        return await getBackend().authMe();
      } catch {
        // Swallow everything to honour "never throws". A network failure
        // is indistinguishable from a signed-out user as far as the SDK
        // caller is concerned — both render the same fallback UI.
        return null;
      }
    },

    users(opts?: ListOptions): Promise<ListPage<BacklitUser>> {
      // Direct delegation — NO try/catch swallow (contrast me() above).
      // users() is privileged; failures must surface as BacklitError so an
      // admin can tell a denial from an empty roster.
      return getBackend().authUsers(opts);
    },

    login(returnPath?: string): void {
      if (!isBacklitHost()) return; // nowhere to navigate off-host
      navigateTo("/_backlit/login?return=" + encodeURIComponent(sanitizeReturnPath(returnPath ?? currentPath())));
    },

    logout(returnPath?: string): void {
      if (!isBacklitHost()) return;
      const url = "/_backlit/logout?return=" + encodeURIComponent(sanitizeReturnPath(returnPath ?? currentPath()));
      const hook = beforeLogout;
      if (!hook) {
        navigateTo(url);
        return;
      }
      if (logoutRun) return; // a logout is already under way: join it
      let settle = () => {};
      let done = false;
      const go = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        logoutRun = null;
        navigateTo(url);
        settle();
      };
      logoutRun = new Promise<void>((r) => (settle = r));
      const timer = setTimeout(go, hook.capMs);
      try {
        hook.fn().then(go, go);
      } catch {
        go();
      }
    },
  };
}

// Exported for direct unit testing of the sanitiser (not part of the public
// window.backlit surface).
export { sanitizeReturnPath as _sanitizeReturnPath };
